// ═══════════════════════════════════════════
// Theme & Config (Pause) — Sprint 23 재설계
// Sprint 20: 자본시장법 대응 (행동지시형 → 데이터 상태형)
// Sprint 23: Pro 플랜 숨김 → Pro 전용 기능을 Core로 승격
//            TIER 수치를 subscriptionService.maxHoldings와 일치
// ═══════════════════════════════════════════
export const T = {
  bg: "#101417", bg2: "#151C1C", card: "#171D21", cardHover: "#1C2328",
  border: "rgba(255, 255, 255, 0.07)",
  text: "#EEF3F1", textDim: "#93A09A", textMuted: "#6B7A74", textSub: "#829088",
  accent: "#1F6B5C", accentHover: "#2B8A78", accentGlow: "rgba(31,107,92,0.12)",
  accentSoft: "#123A33", accentSoftText: "#9FD5C7",
  green: "#10B981", red: "#EF4444", yellow: "#F59E0B", blue: "#3B82F6", cyan: "#06B6D4",
};

// ═══════════════════════════════════════════
// Sprint 20: 상태 라벨 전면 교체
// ═══════════════════════════════════════════
export const STATE = {
  STABLE:     { color: T.green,  bg: "#10B98110", label: "변화 낮음",       icon: "CircleCheck",   desc: "주요 지표의 변동이 설정 범위 내에 있습니다" },
  CHANGE:     { color: T.yellow, bg: "#F59E0B10", label: "변화 발생",       icon: "Eye",           desc: "일부 지표에서 변화가 감지되었습니다" },
  VOLATILE:   { color: T.red,    bg: "#EF444410", label: "변동성 확대",     icon: "AlertTriangle", desc: "단기 가격 변동폭과 거래량이 평시 대비 확대되었습니다" },
  CHECKNEEDED:{ color: T.blue,   bg: "#3B82F610", label: "정보 점검 필요",  icon: "Search",        desc: "현재 데이터만으로 일관된 비교 결과를 제공하기 어렵습니다" },
};

export const STATE_MIGRATION = {
  HOLD: "STABLE",
  WATCH: "CHANGE",
  CAUTION: "VOLATILE",
  PAUSE: "CHECKNEEDED",
};

export function getStateConfig(stateKey) {
  if (STATE[stateKey]) return STATE[stateKey];
  const migrated = STATE_MIGRATION[stateKey];
  if (migrated && STATE[migrated]) return STATE[migrated];
  return STATE.CHANGE;
}

export const MARKET = {
  HOT: { color: T.red, label: "과열 구간" },
  NEUTRAL: { color: T.textDim, label: "중립 구간" },
  COLD: { color: T.cyan, label: "위축 구간" },
};

// ═══════════════════════════════════════════
// Sprint 20: 원칙 설정 관련 상수
// ═══════════════════════════════════════════
export const PRINCIPLE_DEFAULTS = {
  loss_limit_pct: -8,
  target_profit_pct: 15,
  max_hold_days: 90,
  max_volatility_pct: 12,
  disclosure_sensitivity: true,
  news_sensitivity: true,
};

export const DRIFT_STATUS = {
  WITHIN: { color: T.green, label: "범위 내", icon: "Check" },
  DRIFTED: { color: T.red, label: "이탈", icon: "AlertCircle" },
  CHECK: { color: T.yellow, label: "점검 필요", icon: "Search" },
};

// ═══════════════════════════════════════════
// 티어별 수치 제한 (Sprint 26-1: subscriptionGuard.js와 일치)
// - free:    1 종목 / 월 3회 (강한 제한 - 끈만 유지)
// - starter: 3 종목 / 월 30회 (Core 일부 활용)
// - core:    10 종목 / 월 250회 (1일 30회 Soft Cap)
// - pro:     숨김 (내부 로직만 유지, 결제 차단됨)
// ═══════════════════════════════════════════
export const TIER = {
  free:    { level: 0, holdings: 1,    principles: 1,    qa_monthly: 3,    qa_daily: 3 },
  starter: { level: 1, holdings: 3,    principles: 10,   qa_monthly: 30,   qa_daily: 10 },
  core:    { level: 2, holdings: 10,   principles: 9999, qa_monthly: 250,  qa_daily: 30 },
  pro:     { level: 3, holdings: 9999, principles: 9999, qa_monthly: 9999, qa_daily: 999 },
};

// ═══════════════════════════════════════════
// 기능별 요금제 요구사항 (Sprint 26-1: 새 가격 구조 반영)
// 새 한도: free 종목 1 / starter 3 / core 10
//          AI 상담 free 3 / starter 30 / core 250
// ═══════════════════════════════════════════
export const FEATURE_PLAN = {
  market_qa:       { plan: "starter", title: "시장 Q&A",          desc: "시장 전체 질문은 Starter 이상에서 가능합니다" },
  monthly:         { plan: "starter", title: "월간 리포트",        desc: "월간 리포트는 Starter 이상에서 가능합니다" },
  holdings_limit:  { plan: "starter", title: "종목 등록 한도",      desc: "현재 플랜의 종목 한도에 도달했습니다. Starter는 3개, Core는 10개까지 등록 가능합니다." },
  qa_limit:        { plan: "starter", title: "AI 상담 한도",       desc: "이번 달 AI 상담 한도에 도달했습니다. Starter는 월 30회, Core는 월 250회 사용 가능합니다." },
  news_full:       { plan: "starter", title: "뉴스 전체 보기",      desc: "전체 뉴스는 Starter 이상에서 확인할 수 있습니다" },

  health_score:    { plan: "core", title: "데이터 대시보드",     desc: "상세 대시보드는 Core 이상에서 가능합니다" },
  dividend:        { plan: "core", title: "배당 캘린더",          desc: "배당 캘린더는 Core 이상에서 가능합니다" },
  sector_rotation: { plan: "core", title: "섹터 데이터",          desc: "업종별 데이터 비교는 Core 이상에서 가능합니다" },
  sector_analysis: { plan: "core", title: "동종업계 비교",        desc: "같은 업종 종목 비교는 Core 이상에서 가능합니다" },

  // Sprint 23: Pro → Core로 승격
  fair_value:      { plan: "core", title: "밸류에이션 데이터",    desc: "밸류에이션 분석은 Core 이상에서 가능합니다" },
  var_analysis:    { plan: "core", title: "변동성 시뮬레이션",    desc: "변동성 분석은 Core 이상에서 가능합니다" },
  alert_setting:   { plan: "core", title: "원칙 이탈 알림",      desc: "원칙 이탈 알림은 Core 이상에서 가능합니다" },
  ai_care:         { plan: "core", title: "행동 패턴 분석",      desc: "행동 패턴 리포트는 Core 이상에서 가능합니다" },
};

export const STOCK_COLORS = [
  "#3B82F6", "#F59E0B", "#10B981", "#EF4444", "#8B5CF6",
  "#06B6D4", "#EC4899", "#14B8A6", "#F97316", "#84CC16",
  "#A855F7", "#0EA5E9", "#E11D48", "#22D3EE", "#FACC15",
  "#FB923C", "#4ADE80", "#C084FC", "#38BDF8", "#FBBF24",
];

// ═══════════════════════════════════════════
// 권한 체크
// ═══════════════════════════════════════════
export function canAccess(plan, feature) {
  const tier = TIER[plan] || TIER.free;
  const info = FEATURE_PLAN[feature];
  if (!info) {
    console.warn(`[canAccess] 알 수 없는 feature: "${feature}" — 기본 차단됨. FEATURE_PLAN에 추가하세요.`);
    return false;
  }
  const requiredLevel = TIER[info.plan]?.level || 0;
  return tier.level >= requiredLevel;
}
